import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import DynamicTable from '../../components/Table/Table';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Common/Loader';
import { useOkta } from '../../hooks/oktaHook';
import {
  setBusinessUnit,
  setPeriodsData,
  setSelectedCycleObj
} from '../../redux/HomePage/cyclesByBuSlice';
import { triggerNotification } from '../../utils/NotificationContext';
import { setStepperStatus } from '../../redux/FeedbackCollation/feedbackCollationSlice';
import { getStepStatus } from '../FeedbackCollation/servies';
import {
  fetchCycleTriggerData,
  startCycle,
  createCycle,
  editCyclePeriod,
  updateCycle,
  fetchHistoryDetails,
  updateGoalsForCycle,
  fetchPeriodDates
} from './services';
import GoalsModal from '../HomePage/GoalsModal';
import { convertStringToCamelCase } from '../../utils/Utils';
import CycleDrawer from '../HomePage/CycleDrawer';

const CycleTriggerTable = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const cycleData = useSelector((state) => state?.cycle);
  const { selectedCycleObj, periodsData } = cycleData;
  const { userDetails } = useOkta();
  const { roles, gpid } = userDetails;
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const cycleName = newParams.get('cycleName');
  const selectedBu = newParams.get('bu');
  const columns = [
    {
      id: 'cycleName',
      label: 'CYCLE NAME',
      type: 'cycleNameLink'
    },
    {
      id: 'startYearPeriod',
      label: 'CYCLE START (YYYYPP)',
      type: 'text',
      addCell: true
    },
    { id: 'endYearPeriod', label: 'CYCLE END (YYYYPP)', type: 'text', addCell: true },
    { id: 'goals', label: 'GOALS' },
    {
      id: 'startCycle',
      label: 'START CYCLE',
      type: 'start_button',
      onClick: (row, user) => {
        setCycleId(row);
        setRestartRowId(row.id);
        setConfirmAction(row.status === 'Created' ? 'start' : 'restart');
        setIsConfirmModalVisible(true);
      }
    },
    { id: 'status', label: 'RUN STATUS', type: 'status' },
    { id: 'lastModifiedSince', label: 'LAST MODIFIED (GMT)', type: 'date' },
    {
      id: 'moreActions',
      label: 'ACTIONS',
      type: 'cycleActions',
      sortable: false,
      onDelete: ({ id }) => {
        setIsConfirmModalVisible(true);
      },
      onLock: ({ id, lockCycle }) => {
        setConfirmAction('lock');
        setIsConfirmModalVisible(true);
        setLockData(id);
        setLockValue(!lockCycle);
      },
      onEdit: ({ id }) => {
        setLockData(id);
      }
    }
  ];

  const [data, setData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [lockData, setLockData] = useState('');
  const [confirmAction, setConfirmAction] = useState('');
  const [cycleId, setCycleId] = useState(null);
  const [lockValue, setLockValue] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [backdropopen, setbackdropopen] = useState(false);
  const [isCycleEdit, setIsCycleEdit] = useState(false);
  const [editingRowId, setEditingRowId] = useState('');
  const [isTestCycle, setIsTestCycle] = useState(false);
  const [cyclePeriod, setCyclePeriod] = useState([]);
  const [isSteeringRecoCompleted, setIsSteeringRecoCompleted] = useState(false);
  const [isSteeringRecoInProgress, setIsSteeringRecoInProgress] = useState(false);
  const [showCycleSave, setShowCycleSave] = useState(false);
  const [restartRowId, setRestartRowId] = useState('');
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showCycleDrawer, setShowCycleDrawer] = useState(false);

  const bu = selectedBu || localStorage.getItem('bu_name');
  // Generate the data for the dropdown
  useEffect(() => {
    if (bu) {
      fetchPeriodDates(bu).then((res) => dispatch(setPeriodsData(res?.periodRange)));
    }
  }, []);

  useEffect(() => {
    if (data?.length) {
      let tempData = data.length && data.filter((record, i) => !record.testCycle);
      let tempCyclePeriods = [];
      tempData.length &&
        tempData.forEach((p, i) => {
          let temp = { endYearPeriod: p['endYearPeriod'], startYearPeriod: p['startYearPeriod'] };
          tempCyclePeriods.push(temp);
        });
      setCyclePeriod(tempCyclePeriods);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedBu && userDetails?.BUs.length) {
      dispatch(setBusinessUnit(userDetails.BUs[0]));
    }
  }, [userDetails, dispatch]);

  useEffect(() => {
    if (selectedBu && selectedBu !== '') {
      getCycleData(selectedBu);
      setLockData('');
      setLockValue(false);
    }
  }, [refresh]);

  const getCycleData = async (bu) => {
    setbackdropopen(true);
    setIsTableLoading(true);
    try {
      const content = await fetchCycleTriggerData(bu);
      content?.forEach((item) => {
        if (item.id === selectedCycleId) {
          dispatch(setSelectedCycleObj(item));
        }
      });
      setData(content);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setbackdropopen(false);
      setIsTableLoading(false);
    }
  };

  const handleStartCycle = async (row, email) => {
    try {
      await startCycle(row.id, email);
      setRefresh((prev) => !prev);
      setIsConfirmModalVisible(false);
      setShowCycleDrawer(false);
    } catch (error) {
      console.error('Error starting cycle:', error);
    }
  };

  const handleSaveChanges = async (savedData, creteCycleValue) => {
    setShowCycleSave(true);
    if (creteCycleValue == 'create_new') {
      let duplicatePeriod = cyclePeriod.filter(
        (period, i) =>
          period['startYearPeriod'] == savedData['startYearPeriod'] &&
          period['endYearPeriod'] == savedData['endYearPeriod']
      );
      if (duplicatePeriod.length) {
        triggerNotification('Cycle Already Exists for selected Period.', 'error');
        return;
      }
    }
    setbackdropopen(true);

    let params = savedData;
    params.cycleName = `${savedData.cycleName}`;
    params.businessUnitName = selectedBu;
    params.testCycle = creteCycleValue == 'create_test' ? true : false;

    try {
      await createCycle(params, userDetails.email);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setbackdropopen(false);
      setIsTableLoading(false);
      setShowCycleSave(false);
    }
  };

  const handleEditChanges = async (updatedData, editingRowId, row) => {
    const flattenedData = updatedData.map((item) => ({
      id: item?.id,
      startYearPeriod: item?.startYearPeriod?.startYearPeriod,
      endYearPeriod: item?.endYearPeriod?.endYearPeriod,
      threshold: item?.threshold?.threshold
    }));
    let params = {};
    const username = userDetails.email;

    if (flattenedData[0]?.id === editingRowId) {
      params['endYearPeriod'] = flattenedData?.[0]?.endYearPeriod
        ? parseInt(flattenedData?.[0]?.endYearPeriod)
        : row?.[0]?.endYearPeriod;
      params['startYearPeriod'] = flattenedData?.[0]?.startYearPeriod
        ? parseInt(flattenedData?.[0]?.startYearPeriod)
        : row?.[0]?.startYearPeriod;
      params['threshold'] = parseInt(flattenedData?.[0]?.threshold);
      params['id'] = editingRowId;
    }

    try {
      if (params['startYearPeriod'] === null) {
        triggerNotification('Please select Start Period', 'info');
      } else if (params['endYearPeriod'] === null) {
        triggerNotification('Please select End Period', 'info');
      } else if (params['startYearPeriod'] >= params['endYearPeriod']) {
        triggerNotification('Start Period cannot be greater than or equal to End period', 'info');
      } else {
        editCyclePeriod(params, username).then(() => {
          setEditingRowId(null);
          setIsCycleEdit(false);
          getCycleData(selectedBu);
          setShowCycleSave(true);
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleLock = async () => {
    let param = {
      id: lockData,
      lockCycle: lockValue
    };
    try {
      setbackdropopen(true);
      await updateCycle([param], userDetails.email);

      setRefresh((prev) => !prev);
      setIsConfirmModalVisible(false);
    } catch (error) {
      console.error('Error updating data:', error);
    } finally {
      setIsTableLoading(false);
      setbackdropopen(false);
    }
  };

  const getCycleHistory = async (id) => {
    const content = await fetchHistoryDetails(id);
    return content;
  };

  const fetchStepperData = useCallback(async () => {
    try {
      if (userDetails?.gpid && userDetails?.roles[0] && selectedBu && selectedCycleId) {
        const content = await getStepStatus(gpid, roles[0], selectedBu, selectedCycleId);
        dispatch(setStepperStatus(content));
        const { stepperStatus } = content;
        const isSteeringRecoCompleted = stepperStatus.some((task) => {
          return task.taskName === 'Steering Reco' && task.status === 'Completed';
        });
        const isSteeringRecoInProgress = stepperStatus.some((task) => {
          return task.taskName === 'Steering Reco' && task.status === 'In Progress';
        });
        setIsSteeringRecoCompleted(isSteeringRecoCompleted);
        setIsSteeringRecoInProgress(isSteeringRecoInProgress);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  });

  useEffect(() => {
    fetchStepperData();
  }, [selectedBu, userDetails, selectedCycleId]);

  useEffect(() => {
    if (restartRowId && data.length > 0) {
      const matchingRow = data.find((item) => item.id === restartRowId);
      if (matchingRow) {
        if (matchingRow.testCycle) {
          setIsTestCycle(true);
        }
      }
    }
  }, [restartRowId]);

  const setCycleGoal = (data) => {
    const getStructuredFormatData = (data) => {
      let filteredList = data?.reduce((acc, obj) => {
        const key = Object.keys(obj).find((key) => key !== 'id');
        acc[convertStringToCamelCase(key)] = Number(obj[key]);
        return acc;
      }, {});
      return filteredList;
    };
    const { goalDescription, goalFormInputs } = data;
    let filteredList = getStructuredFormatData(goalFormInputs);
    let payloadObj = { ...filteredList, cycleTarget: goalDescription };
    updateGoalsForCycle(selectedCycleObj.id, payloadObj).then((res) => {
      fetchCycleTriggerData(selectedBu).then((res) => setData(res));
    });
  };

  let lockDetails = lockValue ? 'Lock' : 'Unlock';

  const getRestartMsg = () => {
    if (isSteeringRecoCompleted && !isTestCycle) {
      return 'Cannot restart the cycle';
    } else if (isSteeringRecoInProgress) {
      return 'Re-starting completed cycle, are you sure?';
    } else {
      return 'Restart Cycle';
    }
  };
  const modalTitleMap = {
    start: 'Start Cycle',
    restart: getRestartMsg(),
    activate: 'Activate Cycle',
    lock: `${lockDetails} Cycle`,
    delete: 'Delete Cycle'
  };

  const modalTitle = modalTitleMap[confirmAction] || 'Delete Cycle';

  // Text mapping
  const modalTextMap = {
    start: 'Are you sure you want to start this cycle?',
    restart:
      isSteeringRecoCompleted && !isTestCycle ? (
        <>
          <span>
            You cannot restart the cycle as the Steering Recommendation Status is Complete.
          </span>
          <br />
        </>
      ) : isSteeringRecoInProgress ? (
        <>
          <span>
            Restarting the cycle may result in changed algo results. If Voting is already
            in-progress, please ensure that you check the SKUs you have already worked on because
            Voting needs to be restarted once cycle re-run is Completed.
          </span>
          <br />
          <span>Do you want to proceed?</span>
        </>
      ) : (
        <>
          <span>Are you sure you want to restart this cycle?</span>
          <br />
          <span
            style={{
              display: 'inline-block',
              marginTop: '16px',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '19.6px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none'
            }}>
            {"Restarting will override the algorithm's results. Your previous votes will be retained, but we recommend reviewing them for accuracy."}
          </span>
        </>
      ),
    activate: `Are you sure you want to activate "${cycleName}" cycle?`,
    lock: `Are you sure you want to ${lockDetails} this cycle?`,
    delete: 'Are you sure you want to permanently remove this cycle?'
  };

  const modalText = modalTextMap[confirmAction] || modalTextMap.delete;

  // Submit text mapping
  const modalSubmitTextMap = {
    start: 'Start',
    restart: isSteeringRecoCompleted && !isTestCycle ? 'Close' : 'Continue',
    activate: 'Continue',
    lock: lockDetails,
    delete: 'Delete'
  };

  const modalSubmitText = modalSubmitTextMap[confirmAction] || modalSubmitTextMap.delete;

  return (
    <>
      <Box
        component="section"
        sx={{
          p: 2,
          borderRadius: '8px',
          backgroundColor: '#FBFCFE',
          boxShadow: '4px 0px 24px -2px #005CBC14'
        }}>
        <DynamicTable
          originalColumns={columns}
          originalData={data?.filter((item) => item.id === selectedCycleId) || []}
          tableName="Cycles List"
          isSearch={false}
          isAddRow={true}
          isColumnFilter={false}
          density="small"
          yearMonthData={periodsData}
          onSave={(savedData, creteCycleValue) => {
            handleSaveChanges(savedData, creteCycleValue);
          }}
          onEdit={(updatedData, editingRowId, row) => {
            handleEditChanges(updatedData, editingRowId, row);
          }}
          isTableLoading={isTableLoading}
          getCycleHistory={getCycleHistory}
          isCycleEdit={isCycleEdit}
          setIsCycleEdit={(isCycleEdit) => setIsCycleEdit(isCycleEdit)}
          editingRowId={editingRowId}
          setEditingRowId={(editingRowId) => setEditingRowId(editingRowId)}
          setShowCycleSave={(showCycleSave) => setShowCycleSave(showCycleSave)}
          showCycleSave={showCycleSave}
          isSteeringRecoCompleted={isSteeringRecoCompleted}
          handleGoalsModal={(rowdata) => {
            setShowGoalsModal(true);
          }}
          showCycleDrawer={(rowData) => {
            setShowCycleDrawer(true);
          }}
          defaultRowsPerPage={5}
        />
      </Box>

      {isConfirmModalVisible && (
        <ConfirmationModal
          open={isConfirmModalVisible}
          title={modalTitle}
          text={modalText}
          isSteeringRecoCompleted={isSteeringRecoCompleted}
          cancelText={confirmAction === 'restart' && modalSubmitText === 'Close' ? '' : 'Cancel'}
          SubmitText={modalSubmitText}
          onClose={() => {
            setIsConfirmModalVisible(false);
          }}
          onSubmit={async () => {
            if (confirmAction === 'restart' && modalSubmitText === 'Close') {
              setIsConfirmModalVisible(false);
            } else if (confirmAction === 'start' || confirmAction === 'restart') {
              handleStartCycle(cycleId, userDetails.email);
            } else if (confirmAction === 'lock') {
              handleLock();
            } else if (confirmAction === '') {
              console.log('alert');
            }
            setIsConfirmModalVisible(false);
          }}
        />
      )}
      <Loader backdropopen={backdropopen} />
      {Object.keys(selectedCycleObj)?.length > 0 && showCycleDrawer && (
        <CycleDrawer
          showDrawer={showCycleDrawer}
          handleClose={() => setShowCycleDrawer(false)}
          selectedBu={selectedBu}
          selectedCycleObj={data?.filter((item) => item.id === selectedCycleId)?.[0]}
          screenName={'cycle-trigger'}
          handleRunCycle={() => {
            setCycleId(selectedCycleObj);
            setRestartRowId(selectedCycleObj.id);
            setConfirmAction('start');
            setIsConfirmModalVisible(true);
          }}
          isSteeringRecoCompleted={isSteeringRecoCompleted}
          periodsData={periodsData}
        />
      )}
      {Object.keys(selectedCycleObj)?.length > 0 && showGoalsModal && (
        <GoalsModal
          showModal={showGoalsModal}
          handleClose={() => setShowGoalsModal(false)}
          saveGoalInputs={(data) => setCycleGoal(data)}
          cycleGoalDescription={''}
          goalInputs={[]}
          screenName={'cycle-trigger'}
          selectedCycleObj={data?.filter((item) => item?.id === selectedCycleId)?.[0] || {}}
        />
      )}
    </>
  );
};

export default CycleTriggerTable;
